"use client"

// @ts-ignore
import { useQuery } from "@tanstack/react-query"
import { jwtDecode } from "jwt-decode"
import { ArrowRight, DollarSign, Wallet, Zap } from "lucide-react"
import type React from "react"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import useLocalStorageArray from "../hooks/useLocalStorageArray"
import { api } from "../services/api"
import "./BuyTransactionForm.css"

interface TokenAsset {
  id: number
  name: string
  symbol: string
  logo_url: string
  is_depositable: boolean
}

const BuyTransactionForm: React.FC = () => {
  const [amount, setAmount] = useState("")
  const [networks, setNetworks] = useState<any[]>([])
  const [selectedNetwork, setSelectedNetwork] = useState<any | null>(null)
  // const [tokens, setTokens] = useState([])
  const [selectedToken, setSelectedToken] = useState<any | null>(null)
  const [walletAddress, setWalletAddress] = useState("")
  const [quote, setQuote] = useState<any | null>(null)
  const [debounceTimeout, setDebounceTimeout] = useState<any | null>(null)
  const [loading, setLoading] = useState(false)

  const [prevAddresses, addPrevAddress] = useLocalStorageArray("buyForm-address", [])

  const [addressError, setAddressError] = useState<string | null>(null)
  const [amountError, setAmountError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [transactionLimitId, setTransactionLimitId] = useState<number | null>(null)

  const navigate = useNavigate()

  // Helper function to format currency with commas
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  // Retrieve the auth token and decode it to get transactionLimitId
  useEffect(() => {
    const token = localStorage.getItem("authToken")
    if (token) {
      try {
        const decoded: any = jwtDecode(token)
        setTransactionLimitId(decoded.transactionLimit)
      } catch (error: any) {
        console.error("Error decoding auth token:", error)
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to decode authorization token. Please try again later."
        setError(errorMessage)
      }
    }
  }, [])

  // Fetch networks on component mount
  useEffect(() => {
    const fetchNetworks = async () => {
      try {
        const response = await api.get(`/networks`)
        setNetworks(response.data)
        setError(null)
      } catch (error: any) {
        console.error("Error fetching networks:", error)
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to fetch networks. Please try again later."
        setError(errorMessage)
      }
    }
    fetchNetworks()
  }, [])

    // Fetch all assets for all networks in parallel
    const {
      data: allRampAssets = {},
      // isLoading: loadingRampAssets,
      // error: rampAssetsError,
    } = useQuery<Record<number, TokenAsset[]>>({
      queryKey: ["allRampAssets", networks.map((n) => n.id)],
      queryFn: async () => {
        if (!networks.length) return {}
  
        // Fetch payment assets for all networks in parallel
        const promises = networks.map((network) =>
          api
            .get(`/networks/${network.id}/assets`)
            .then((response) => {
              const depositableAssets = response.data.filter(
                (asset: TokenAsset) => asset.is_depositable
              )
              return { networkId: network.id, assets: depositableAssets }
            })
            .catch((error) => {
              console.warn(`Failed to fetch assets for network ${network.id}:`, error)
              return { networkId: network.id, assets: [] }
            }),
        )
  
        const results = await Promise.all(promises)
  
        // Convert array to object for easy lookup
        return results.reduce(
          (acc, { networkId, assets }) => {
            if (assets.length > 0) {
              acc[networkId] = assets
            }
            return acc
          },
          {} as Record<number, TokenAsset[]>,
        )
      },
      enabled: networks.length > 0,
      staleTime: 1000 * 60 * 10,
    })

  // Get tokens for the selected network from our pre-fetched data
  const tokens = selectedNetwork ? allRampAssets[selectedNetwork.id] || [] : []

  // Fetch tokens when network is selected
  // useEffect(() => {
  //   if (selectedNetwork) {
  //     const fetchTokens = async () => {
  //       try {
  //         const response = await api.get(`/networks/${selectedNetwork.id}/assets`)
  //         setTokens(response.data)
  //       } catch (error: any) {
  //         console.error("Error fetching tokens:", error)
  //         const errorMessage =
  //           error.response && error.response.data && error.response.data.message
  //             ? error.response.data.message
  //             : "Failed to fetch tokens. Please try again later."
  //         setError(errorMessage)
  //       }
  //     }
  //     fetchTokens()
  //   }
  // }, [selectedNetwork])

  const validateWalletAddress = (address: string, network: string) => {
    if (!address) return true
    if (network === "Solana") {
      const solanaAddressRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/
      return solanaAddressRegex.test(address)
    } else if (
      network === "Optimism Sepolia" ||
      network === "Base" ||
      network === "Base Sepolia" ||
      network === "Optimism" ||
      network === "Arbitrum"
    ) {
      const evmAddressRegex = /^0x[a-fA-F0-9]{40}$/
      return evmAddressRegex.test(address)
    }
    return false
  }

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const address = e.target.value
    setWalletAddress(address)
  }

  useEffect(() => {
    if (walletAddress && selectedNetwork?.name) {
      if (!validateWalletAddress(walletAddress, selectedNetwork.name)) {
        setAddressError("Invalid wallet address for the selected network.")
      } else {
        setAddressError(null)
        addPrevAddress(walletAddress)
      }
    } else {
      setAddressError(null)
    }
  }, [walletAddress, selectedNetwork])

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setAmount(value)

    const numericValue = Number.parseFloat(value)
    let amountMaxLimit = 100
    switch (transactionLimitId) {
      default:
      case 1:
        amountMaxLimit = 100
        break
      case 2:
      case 3:
        amountMaxLimit = 2500
        break
      case 4:
        amountMaxLimit = 5000
        break
      case 5:
        amountMaxLimit = 10000
        break
      case 6:
        amountMaxLimit = 25000
        break
    }
    if (isNaN(numericValue) || numericValue < 10 || numericValue > amountMaxLimit) {
      setAmountError(`Amount must be between 10 and ${formatCurrency(amountMaxLimit)} MXN.`)
    } else {
      setAmountError(null)
    }
  }

  const fetchQuote = useCallback(() => {
    if (amount && selectedToken && selectedNetwork) {
      const numericValue = Number.parseFloat(amount)
      let amountMaxLimit = 100
      switch (transactionLimitId) {
        default:
        case 1:
          amountMaxLimit = 100
          break
        case 2:
        case 3:
          amountMaxLimit = 2500
          break
        case 4:
          amountMaxLimit = 5000
          break
        case 5:
          amountMaxLimit = 10000
          break
        case 6:
          amountMaxLimit = 25000
          break
      }
      if (numericValue >= 10 && numericValue <= amountMaxLimit) {
        setLoading(true)
        api
          .post("/quote", {
            toChain: selectedNetwork.name,
            toToken: selectedToken.symbol,
            amount: numericValue,
            direction: "buy",
          })
          .then((response) => {
            setQuote(response.data.data)
            setLoading(false)
          })
          .catch((error) => {
            console.error("Error fetching quote:", error)
            setLoading(false)
            const errorMessage =
              error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : "Failed to fetch quote. Please try again later."
            setError(errorMessage)
          })
      }
    }
  }, [amount, selectedNetwork, selectedToken, transactionLimitId])

  useEffect(() => {
    if (amount && selectedToken && selectedNetwork) {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout)
      }
      const timeout = setTimeout(() => fetchQuote(), 500)
      setDebounceTimeout(timeout)
      return () => clearTimeout(timeout)
    }
  }, [amount, selectedToken, selectedNetwork, fetchQuote])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const token = localStorage.getItem("authToken")
    if (token) {
      try {
        const decoded: any = jwtDecode(token)
        if (decoded.kycStatus !== "accepted") {
          navigate("/kyc")
          return
        }
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`
      } catch (error) {
        console.error("Error decoding token:", error)
        navigate("/auth")
        return
      }
    } else {
      navigate("/auth")
      return
    }

    if (amount && selectedToken && selectedNetwork && walletAddress && !addressError && !amountError) {
      try {
        const response = await api.post("/ramp/buy", {
          amount,
          token: selectedToken.symbol,
          network: selectedNetwork.name,
          to_onchain: walletAddress,
        })
        navigate(`/transactions/${response.data.txId}`)
      } catch (error: any) {
        console.error("Error submitting transaction:", error)
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to submit transaction. Please try again later."
        setError(errorMessage)
      }
    }
  }

  // Comprehensive validation function
  const isFormValid = () => {
    // Check if all required fields are present
    if (!walletAddress || !selectedNetwork || !selectedToken || !amount) {
      return false
    }

    // Check if there are any validation errors
    if (addressError || amountError) {
      return false
    }

    // Check if quote is available and not loading
    if (!quote || loading) {
      return false
    }

    // Check if amount is valid number
    const numericValue = Number.parseFloat(amount)
    if (isNaN(numericValue) || numericValue <= 0) {
      return false
    }

    // Check if wallet address is valid for selected network
    if (!validateWalletAddress(walletAddress, selectedNetwork.name)) {
      return false
    }

    return true
  }

  // Clear quote when network or token changes
  useEffect(() => {
    if (quote && (selectedNetwork || selectedToken)) {
      setQuote(null)
    }
  }, [selectedNetwork, selectedToken])

  return (
    <div className="buy-form-container">
      <div className="buy-form-card">
        <div className="buy-form-header">
          <h2>
            <DollarSign className="header-icon" />
            Compra crypto con MXN
          </h2>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="buy-form">
          {/* Wallet Address Section */}
          <div className="form-section">
            <h3>
              <Wallet className="section-icon" />
              Dirección de wallet *
            </h3>
            <div className="form-group">
              <input
                type="text"
                list="address-suggestions"
                value={walletAddress}
                onChange={handleAddressChange}
                placeholder="A dónde enviamos tu crypto?"
                className="form-input"
                required
              />
              <datalist id="address-suggestions">
                {prevAddresses.map((val, idx) => (
                  <option key={idx} value={val} />
                ))}
              </datalist>
              {addressError && <span className="field-error">{addressError}</span>}
            </div>
          </div>

          {/* Network Selection */}
          <div className="form-section">
            <h3>Red de destino *</h3>
            <div className="networks-grid">
              {networks
              .filter((network: any) => allRampAssets.hasOwnProperty(network.id))
              .map((network: any) => (
                <div
                  key={network.id}
                  className={`network-card ${selectedNetwork?.id === network.id ? "selected" : ""}`}
                  onClick={() => {
                    setSelectedNetwork(network)
                    setSelectedToken(null) // Reset token when network changes
                  }}
                >
                  <div className="network-logo">
                    <img
                      src={network.logo_url || "/placeholder.svg"}
                      alt={network.name}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.src = "/placeholder.svg"
                      }}
                    />
                  </div>
                  <p className="network-name">{network.name}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Token Selection */}
          {selectedNetwork && (
            <div className="form-section">
              <h3>Token *</h3>
              <div className="tokens-grid">
                {tokens
                // .filter((token: any) => token.is_depositable)
                .map((token: any) => (
                  <div
                    key={token.id}
                    className={`token-card ${selectedToken?.id === token.id ? "selected" : ""}`}
                    onClick={() => setSelectedToken(token)}
                  >
                    <div className="token-logo">
                      <img
                        src={token.logo_url || "/placeholder.svg"}
                        alt={token.symbol}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.src = "/placeholder.svg"
                        }}
                      />
                    </div>
                    <div className="token-info">
                      <div className="token-symbol">{token.symbol}</div>
                      {/* <div className="token-name">{token.name}</div> */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Amount Section */}
          <div className="form-section">
            <h3>Monto a enviar (MXN) *</h3>
            <div className="form-group">
              <input
                type="number"
                value={amount}
                onChange={handleAmountChange}
                placeholder="Ingresa el monto"
                className="form-input"
                min="10"
                step="0.01"
                required
              />
              {amountError && <span className="field-error">{amountError}</span>}
            </div>
          </div>

          {/* Quote Section */}
          {selectedNetwork && selectedToken && (
            <div className="quote-section">
              <div className="quote-card">
                <div className="quote-header">
                  <Zap className="quote-icon" />
                  <h4>Cotización en tiempo real</h4>
                </div>
                <div className="quote-content">
                  {loading ? (
                    <div className="quote-loading">
                      <div className="loading-spinner"></div>
                      <span>Calculando mejor precio...</span>
                    </div>
                  ) : quote ? (
                    <div className="quote-details">
                      <div className="quote-amount">
                        {quote.quoteAmount} {quote.quoteAsset}
                      </div>
                      <div className="quote-rate">@ {quote.quoteRate} MXN</div>
                      <div className="quote-network">en {selectedNetwork?.name}</div>
                    </div>
                  ) : amount ? (
                    <div className="quote-placeholder">Ingresa un monto válido para ver la cotización</div>
                  ) : (
                    <div className="quote-placeholder">—</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className={`submit-button ${isFormValid() ? "enabled" : "disabled"}`}
            disabled={!isFormValid()}
          >
            {loading ? (
              <>
                <div className="loading-spinner small"></div>
                Procesando...
              </>
            ) : (
              <>
                Confirmar compra
                <ArrowRight className="btn-icon" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

export default BuyTransactionForm
